import { ROLES } from 'app/configs/roles';
import { ExternalSourceIDSetBy } from 'app/interfaces/ComplianceTransaction';
import { INCOMPLETE_COMPLIANCE_DOC_STATUSES } from 'app/configs/document';
import { DOC_OWNER, DocOwnerValue } from 'app/configs/docOwner';
import { AppURLs } from 'app/utils/appURLs';
import { MODULE_IDS } from 'app/pages/Agent/Dashboard/components/types';
import { getPartnerData } from 'app/utils/localStorageHandler/partnerData';
import {
  getAgentProfile,
  getUserRoles,
  getPartnerCompanyName,
} from 'app/utils/localStorageHandler/userProfile';
import { FilterValues } from 'app/pages/Compliance/Dashboard/pages/transactions/filterHandler';
import { TAG_NAMES } from 'app/components/common/Tags/tagUtils';
import { Partners, PartnerStates } from '@maxhomeai/maxhome-configs';
export const PARTNERS = Partners;

const parseShowComplianceCheckEnv = (raw?: string): string[] | undefined => {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) {
      return parsed;
    }
    console.error('showComplianceCheck env var must be a JSON string array');
  } catch {
    console.error('Failed to parse showComplianceCheck env var');
  }
  return undefined;
};

// PartnerStates is a generated object literal with literal keys, so indexing it
// by a plain string is TS7053 under strict mode. The backend casts identically.
const statesFor = (companyName?: string | null): readonly string[] =>
  (PartnerStates as Record<string, readonly string[]>)[companyName ?? ''] ?? [];

export const isMultiStatePartner = (
  companyName: string | null | undefined = getPartnerCompanyName()
): boolean => statesFor(companyName).length > 1;

export const getPartnerStateOptions = (
  companyName: string | null | undefined = getPartnerCompanyName()
): string[] => [...statesFor(companyName)];

// V2 Transaction Type Enums
// TODO: Fetch these dynamically from backend in the future
export enum TRANSACTION_PROPERTY_TYPES {
  RESIDENTIAL = 'RESIDENTIAL',
  COMMERCIAL = 'COMMERCIAL',
  CONDO = 'CONDO',
  LAND_LOT = 'LAND_LOT',
  LOT_RESERVATION = 'LOT_RESERVATION',
  NEW_CONSTRUCTION = 'NEW_CONSTRUCTION',
  OTHER = 'OTHER',
  FARM = 'FARM',
}

export enum TRANSACTION_REPRESENTATION_TYPES {
  BUYER = 'BUYER',
  SELLER = 'SELLER',
  BOTH = 'BOTH',
  OTHER = 'OTHER',
}

export enum TRANSACTION_DEAL_TYPES {
  PURCHASE = 'PURCHASE',
  PURCHASE_AS_IS = 'PURCHASE_AS_IS',
  LEASE = 'LEASE',
  NEW_CONSTRUCTION = 'NEW_CONSTRUCTION',
  OUTBOUND_REFERRAL = 'OUTBOUND_REFERRAL',
  OTHER = 'OTHER',
  BPO = 'BPO',
  REFERRAL = 'REFERRAL',
  APT_RENTAL = 'APT_RENTAL',
  LEASE_RENEWAL = 'LEASE_RENEWAL',
  FSBO = 'FSBO',
  OUTBOUND_RELOCATION = 'OUTBOUND_RELOCATION',
}

/**
 * Configuration for the external source link displayed in transaction details.
 * Controls visibility, label, and link type (e.g., Dotloop, Skyslope).
 */
export interface ExternalSourceLinkConfig {
  show: boolean;
  label: string;
  urlTemplate?: string;
  // Overrides keyed by the transaction's externalSourceIDSetBy, for sources
  // where the same id space covers records that live at different URLs.
  // Falls back to urlTemplate/label when unset or unmapped.
  variantsBySetBy?: Record<string, { label?: string; urlTemplate: string }>;
}

// How Resync availability is decided for a partner.
// 'externalSourceId' — enabled when the transaction has an externalSourceID.
// 'linkedTdTransaction' — enabled when a linked Transaction Desk record exists.
export type ResyncAvailabilityStrategy =
  | 'externalSourceId'
  | 'linkedTdTransaction';

export const buildExternalSourceUrl = (
  linkConfig: ExternalSourceLinkConfig | undefined,
  externalSourceID: string | null | undefined,
  externalSourceIDSetBy?: string | null
): string | null => {
  const template = externalSourceIDSetBy
    ? (linkConfig?.variantsBySetBy?.[externalSourceIDSetBy]?.urlTemplate ??
      linkConfig?.urlTemplate)
    : linkConfig?.urlTemplate;
  if (!template || !externalSourceID) return null;
  return template.replace(
    '{externalSourceID}',
    encodeURIComponent(externalSourceID)
  );
};

// Label for the external source link, which can differ per record kind for the
// same partner (e.g. Christie deal folder vs property folder).
export const resolveExternalSourceLabel = (
  linkConfig: ExternalSourceLinkConfig | undefined,
  externalSourceIDSetBy?: string | null
): string =>
  (externalSourceIDSetBy
    ? linkConfig?.variantsBySetBy?.[externalSourceIDSetBy]?.label
    : undefined) ??
  linkConfig?.label ??
  '';

// Resolves the external source deep-link for a transaction, applying
// partner-specific URL schemes (Paperless, Skyslope, Dotloop). Returns null
// when there is no link to open (e.g. Keyes shows a copyable deal number).
export const resolveExternalSourceUrl = (
  companyName: string | null | undefined,
  linkConfig: ExternalSourceLinkConfig | undefined,
  externalSourceID: string | null | undefined,
  externalSourceIDSetBy?: string | null
): string | null => {
  if (!externalSourceID) return null;
  switch (companyName) {
    case PARTNERS.KEYES_COMPANY:
      return null;
    case PARTNERS.ARC_REALTY:
    case PARTNERS.CAPSTONE:
    case PARTNERS.COASTAL:
    case PARTNERS.CENTURY21_REDWOOD:
      return `https://app.paperlesspipeline.com/tx/view/${encodeURIComponent(externalSourceID)}`;
    case PARTNERS.WATSON_REALTY: {
      const isNumeric = /^\d+$/.test(externalSourceID);
      const encodedID = encodeURIComponent(externalSourceID);
      return isNumeric
        ? `https://app.skyslope.com/ListingChecklist.aspx?id=${encodedID}&Type=edit`
        : `https://app.skyslope.com/TransactionChecklist.aspx?TransactionID=${encodedID}&ListingID=MA==&checklistId=MA==`;
    }
    default:
      return (
        buildExternalSourceUrl(
          linkConfig,
          externalSourceID,
          externalSourceIDSetBy
        ) ?? `https://www.dotloop.com/m/loop?viewId=${externalSourceID}`
      );
  }
};

export type SOTWidget =
  | { kind: 'select'; options: string[] }
  | { kind: 'radio'; options: Array<{ label: string; value: boolean }> }
  | {
      kind: 'booleanGroup';
      fields: Array<{ fieldKey: string; label: string }>;
    };

/**
 * Configuration for extra SOT fields to display in transaction details.
 * Use `widget` to override the default text/date rendering.
 */
export interface ExtraSOTField {
  fieldKey: string;
  // Synonymous backend SOT keys; the first one present in the payload is used.
  aliasKeys?: string[];
  // Render only when the payload contains the field's key or one of its aliases.
  hideWhenEmpty?: boolean;
  label?: string;
  /** SOT backend type hint. Omit when using `widget`. */
  type?: 'string' | 'list' | 'date';
  /** Render this field in a "copyable" pill (like Deal Number) */
  copyable?: boolean;
  /** Override the default widget. Mutually exclusive options live here. */
  widget?: SOTWidget;
  /** Conditions that must be met to render this field */
  renderCondition?: {
    representationTypes?: string[];
    transactionTypes?: string[];
    dealTypes?: string[];
    /** Only show at this exact milestone */
    milestone?: string;
    /** Show at or after this milestone (uses milestone ordering) */
    minMilestone?: string;
    /** Show at or before this milestone (uses milestone ordering) */
    maxMilestone?: string;
  };
}

// Reads a `base` transaction field, a `sot` data-key fallback chain (first
// non-null wins, since SOT keys vary by state), or `sumSot` (adds every key
// that has a value — for a total split across per-side SOT keys, e.g.
// list-side % + buy-side %). Exactly one of the three is required.
// `docField`/`sumDocField` hold `document.<TYPE>.rawExtract.<field>` keys —
// same chain semantics as `sot`, read off one document type.
type ReportColumnDocSource = { docField?: never; sumDocField?: never };
type ReportColumnSource =
  | ({ base: string; sot?: never; sumSot?: never } & ReportColumnDocSource)
  | ({ sot: string[]; base?: never; sumSot?: never } & ReportColumnDocSource)
  | ({ sumSot: string[]; base?: never; sot?: never } & ReportColumnDocSource)
  | {
      docField: string[];
      base?: never;
      sot?: never;
      sumSot?: never;
      sumDocField?: never;
    }
  | {
      sumDocField: string[];
      base?: never;
      sot?: never;
      sumSot?: never;
      docField?: never;
    };

export type ReportColumn = ReportColumnSource & {
  label: string;
  format?: 'date' | 'currency' | 'percent';
  // Offer in the report's show/hide picker; hiding also drops it from the sheet.
  hideable?: boolean;
  // Start hidden; implies `hideable`.
  defaultHidden?: boolean;
  // Every row on this tab is expected to carry the field (a listing has a list
  // date, a closing a close date) — render a blank as a warning, not an em dash.
  flagWhenMissing?: boolean;
};

// `sum` totals a SOT value; `gci` = Σ price × pct / 100. Every key list is a
// fallback chain resolved per transaction the same way `ReportColumn.sot` is.
// `pctMode: 'sum'` instead adds the percent keys — a brokerage that earns both
// sides (Tomlinson) wants list% + buy%, one that does not would double-count.
export interface ReportTotal {
  label: string;
  sum?: string[];
  gci?: { price: string[]; pct: string[]; pctMode?: 'first' | 'sum' };
}

export interface ReportTab {
  key: string;
  label: string;
  filters?: {
    representationTypes?: string[];
    milestones?: string[];
    // Transactions with no active document of any of these types (e.g. Missing BBA).
    missingDocTypes?: string[];
  };
  columns: ReportColumn[];
  totals?: ReportTotal[];
  // SOT price field chain driving the "minimum $" filter/sort (Top Sales etc.).
  amountField?: string[];
  // Initial value for the "minimum $" input.
  defaultMinAmount?: number;
  // 'agentLeaderboard' renders agents ranked by summed deal value instead of rows.
  // 'comparison' renders `totals` per period against the preceding period.
  variant?: 'agentLeaderboard' | 'comparison';
  // Initial grain for the 'comparison' variant. Defaults to 'month'.
  defaultGrain?: 'month' | 'quarter' | 'year';
}

// `enabled` gates whether the transactions report widget shows at all.
export interface TransactionReportConfig {
  enabled?: boolean;
  tabs?: ReportTab[];
}

// Incomplete Compliance report config.
export interface IncompleteComplianceConfig {
  enabled?: boolean;
  // Range picker's initial window length. Defaults to 7.
  closingWithinDays?: number;
  statuses?: string[];
  // Also count transactions missing a required doc.
  includeMissingDocs?: boolean;
  columns?: ReportColumn[];
}

export interface ModuleConfig {
  id: string;
  enabled: boolean;
  roles: string[];
  children?: ModuleConfig[];
}

export interface FilterPreset {
  id: string;
  label: string;
  tooltip?: string;
  filters: Partial<FilterValues>;
}

/** A user-saved transaction filter, scoped per-profile (current role). */
export enum SavedFilterType {
  COMPLIANCE_TRANSACTION_FILTER = 'COMPLIANCE_TRANSACTION_FILTER',
}

export interface SavedTransactionFilter {
  _id: string;
  filterType: SavedFilterType;
  name: string;
  filters: Partial<FilterValues>;
  createdAt: string;
  updatedAt: string;
}

export interface AgreementFormItem {
  /** Stable key for the dropdown item (e.g. transactionType or formId). */
  key: string;
  /** Human-readable label shown in the dropdown. */
  label: string;
  /** Resolved URL to navigate to when selected (from AppURLs). */
  path: string;
}

export interface PartnerConfig {
  /**
   * Rule: Feature flags are arrays of allowed roles.
   * Example: canEditClosingDate?: string[]
   * - If array is empty or undefined, feature is disabled (no roles can access)
   * - If array has roles, only those roles can access the feature
   * - Admin only has access if ROLES.ADMIN is explicitly in the array
   */
  canEditClosingDate?: string[];
  /** Roles that can edit transaction address/name */
  canEditTransactionAddress?: string[];
  /** Roles that can use grouped task templates and task UI */
  useGroupTaskTemplates?: string[];
  /** Timezone for partner */
  timezone?: string;
  /** Roles that can see the Compliance Check button and column in agent workspace */
  showComplianceCheck?: string[];
  /** Roles that can see Reporting in the agent workspace */
  showAgentReporting?: string[];
  /** When Compliance Check is hidden, "Submit for Processor Review" emails the docs to the partner's processors instead. */
  emailProcessorReviewWithoutCompliance?: boolean;
  /** Enable document upload from compliance transaction details page */
  enableComplianceDocumentUpload?: boolean;
  /**
   * Document navigation: displayStatus values to exclude from prev/next navigation in verify-document page (compliance).
   * Default when unset: ['PROCESSING'].
   */
  excludedDocumentStatusesInComplianceNavigation?: string[];
  /** Enable V2 transaction types */
  transactionTypeV2Enabled?: boolean;
  /** Enable split listing milestones for listing transactions */
  splitListingMilestones?: boolean;
  /** Milestones to exclude per legacy transaction type (PURCHASE/LISTING/LEASE_OFFER/LEASE_LISTING) */
  excludedMilestonesByTransactionType?: Record<string, string[]>;
  /** Milestones to exclude per representation type (BUYER/SELLER/BOTH); defaults hide listing milestones for buy-side and Pre-Contract for list/dual */
  excludedMilestonesByRepresentationType?: Record<string, string[]>;
  /** Roles that can see the folder view toggle and use folder organization */
  showFolderView?: string[];
  /**
   * When folder view is enabled, default document list view.
   * 'list' = documents (list) view by default; 'folder' or omit = folder view by default.
   */
  defaultDocumentListViewMode?: 'list' | 'folder';
  /** Show Task Template Badge in Task Views */
  showTemplateBadgeInTaskView?: string[];
  /** Roles that can use checkbox selection in folder view */
  enableFolderViewCheckboxSelection?: string[];
  /** Property types that can be used in V2 transaction types */
  propertyTypes?: string[];
  /** Representation types that can be used in V2 transaction types */
  representationTypes?: string[];
  /** Deal types that can be used in V2 transaction types */
  dealTypes?: string[];
  /** Enable search and update user feature in Add User modal for Admin role */
  searchAndUpdateUser?: string[];
  extraSOTFields?: ExtraSOTField[];
  // Transactions report widget in compliance reporting; `tabs` fall back to the
  // default when unset.
  transactionReport?: TransactionReportConfig;
  // Incomplete Compliance report tab; falls back to defaults when unset.
  incompleteCompliance?: IncompleteComplianceConfig;
  externalSourceLink?: ExternalSourceLinkConfig;
  /** Whether this partner supports resyncing data from an external source. */
  externalSourceResync?: boolean;
  /**
   * How Resync availability is decided. Defaults to 'externalSourceId' when
   * unset. Only consulted when externalSourceResync is true.
   */
  resyncAvailabilityStrategy?: ResyncAvailabilityStrategy;
  /**
   * Prefer the errorFields API vertices over document base config
   * coordinates when positioning document-error overlays. Defaults to
   * true; set to false to fall back to base config coordinates.
   */
  preferApiErrorVertices?: boolean;

  /** Roles that can delete tags from transactions */
  canDeleteTags?: string[];
  /** Roles that can remove/clear SOT field values */
  canRemoveSOTValue?: string[];
  /** Roles that can view SOT field values */
  canViewSOTHistory?: string[];

  moduleConfigs: ModuleConfig[];

  /** Roles allowed to create transactions from UI */
  canCreateComplianceTransaction?: string[];
  /** Enable document ownership tracking on missing documents */
  enableDocumentOwnership?: boolean;
  /** Which document owner roles are available for this partner. Defaults to ['AGENT', 'PROCESSOR']. */
  documentOwnerRoles?: DocOwnerValue[];
  /** Compliance Login Redirect */
  complianceLoginRedirect?: string;
  /** Closing Member Login Redirect */
  closingMemberLoginRedirect?: string;
  /** Roles that can retrigger tasks */
  canRetriggerTasks?: string[];
  /** Roles that can edit a task email's pre-built content (the "Edit Content" button). */
  canEditTaskEmailContent?: string[];
  /** Custom filter presets for the transaction filter drawer */
  customFilterPresets?: FilterPreset[];
  /**
   * Label for the "Dual Agency Only" toggle in the transaction filter drawer.
   * Defaults to 'Dual Agency Only' when unset.
   */
  dualAgencyFilterLabel?: string;
  /** Enable listing fall-through workflow: confirmation modal + new listing workspace creation */
  enableFallThroughWorkflow?: boolean;
  /** Enable purchase fall-through workflow: confirmation modal + duplicate transaction creation for purchases */
  enablePurchaseFallThroughWorkflow?: boolean;
  /** Whether to show the MLS Number field in transaction details. */
  showMlsNumberField?: boolean;
  /** Whether to show Financing Type in transaction additional details. Defaults to false. */
  showFinancingTypeField?: boolean;
  /** Whether to show Referring Firm in transaction additional details. Defaults to false. */
  showReferringFirmField?: boolean;
  /**
   * Show a badge in the transaction details header when earnest money is held
   * in-house. The holder name is read from the `earnestMoneyHolder` SOT field
   * (extracted from the purchase contract) and matched against `holderMatch`
   * (case-insensitive substring). Absent = no badge.
   */
  earnestMoneyHeldIndicator?: {
    holderMatch: string;
    label: string;
  };
  /**
   * Whether to show each assigned agent's team and team lead on the
   * transaction details page. Requires the backend to populate
   * `agents[].details.team` / `agents[].details.teamLeadName`. Defaults to false.
   */
  showAgentTeamField?: boolean;
  /**
   * Always render the Profit Power Sale ID and Profit Power Status fields in
   * transaction additional details. When true, missing values render as
   * "Not Found" instead of being hidden. Defaults to false.
   */
  showProfitPowerFields?: boolean;
  /**
   * Always render the Accounting ID (Dotloop transaction number) field in
   * transaction additional details. When true, a missing value renders as
   * "—" instead of hiding the field. Defaults to false.
   */
  showAccountingID?: boolean;
  /** Show closing team quick filters (Closing Team Ready / Closed Incomplete) in the tasks view */
  showClosingTeamQuickFilters?: boolean;
  /**
   * Permission keys (e.g. 'canEditClosingDate') where CLOSING_MEMBER should NOT
   * inherit COMPLIANCE access. Leave empty to inherit all COMPLIANCE permissions.
   */
  closingMemberExclusions?: string[];
  /** Show Board column on admin management page */
  showBoardColumn?: boolean;
  /** Show the workspace creator and creation timestamp on agent transaction tiles. */
  showTransactionTileMetadata?: boolean;
  /** Whether to show the "Sync to linked transaction?" dialog */
  syncLinkedDocumentStatus?: boolean;
  /** Whether to offer syncing a manually updated SOT field to the linked transaction */
  syncLinkedSotFields?: boolean;
  /** Roles that can see the "All Transactions" tab on the compliance transactions page. When unset, all users can see it. */
  showAllTransactionsTabToRoles?: string[];
  /** Items shown in the "Start a New Agreement" dropdown. Absent or empty = no items rendered. */
  agreementForms?: AgreementFormItem[];
  /** Config-driven offer form shown on both of Create Offer's branches — prefilled from the listing on the MLS branch. Absent = the non-MLS branch creates the offer directly and the MLS branch skips the form entirely. */
  offerFormId?: string;
  /**
   * Create Offer on lease (tenant-side) transactions. Defaults to true; set
   * false to hide it. The contract it drafts comes from the partner's
   * `offerContractGroups`, which are purchase contracts today.
   */
  leaseOfferDrafting?: boolean;
  /** Task-related feature flags. Each key is a roles array following the same rule as top-level flags. */
  taskConfigs?: {
    /** Roles that can add an ad-hoc task inside a transaction's task list */
    addTaskInTransaction?: string[];
  };
  allowedSigningOrders?: string[];
  /**
   * Brokerage Settings → Folder Config page.
   * `view: 'legacy'` shows mapping rules as bare transaction-type buckets
   * (Purchase/Listing/Lease Offer/Lease Listing) translated to dealType +
   * representationType pairs; `'v2'` shows the full property/deal/rep editor
   * (contract-type-based partners get a simple doc→folder editor regardless,
   * derived from their configType). Visible to users whose role is in `roles`
   * OR whose email is in `emails` — nothing implicit, so a partner override
   * must include ROLES.ADMIN to keep internal Admin access.
   */
  folderConfigUI?: {
    view: 'legacy' | 'v2';
    roles: string[];
    emails: string[];
  };
  // Enable the in-house signing experience instead of the legacy DocuSeal embed.
  enableInhouseSigning?: boolean;
  // Partner opt-in for transaction links in agent-facing emails; the
  // enable_transaction_link_in_agent_emails feature flag is also required.
  showTransactionLinkInAgentEmails?: boolean;
  // Partner opt-in for the Payments tab — the only gate on showing it.
  // Whether a deposit can actually be sent depends on the brokerage having an
  // Authvia merchant, which the tab reports rather than hiding itself.
  enablePayments?: boolean;
  // CTA to continue to signing despite required compliance errors (on by default).
  allowSigningWithComplianceErrors?: boolean;
  // Partner opt-in for the Daily Hub v2 — the hub, the document viewer and the full-screen task
  // detail, which only work as a set. The daily_hub_v2 flag is the other half.
  enableDailyHubV2?: boolean;
  // Partner opt-in for the documents checklist on the compliance transaction page. Separate from
  // daily_hub_v2 above because it can ship on its own. The document_checklist flag is the other
  // half.
  enableDocumentChecklist?: boolean;
  // Partner opt-in for Ask Max; mirrors backend partnerConfig.askMaxEnabled. The ask_max flag is
  // the other half.
  enableAskMax?: boolean;
  /**
   * Roles available for manual assignment in Team Hub, keyed by WORK_ITEM_ROLE.
   * Mirrors backend partnerConfig.dailyHub.roleSlots.
   * Absence of a role key means that selector is hidden.
   * Example: { CLOSING_TEAM: true }
   */
  dailyHubRoleSlots?: { PROCESSING_TEAM: true } & Partial<
    Record<'CLOSING_TEAM', true>
  >;
  dailyHubDefaultPageSize?: number;
}

// Default report tabs (used when a partner enables the report without its own
// tabs). SOT key chains come from a survey of the SOT schemas — first non-null
// wins. `PRICE`/`COMMON` are shared across tabs; partners override any of it.
const PRICE = ['purchasePriceGross', 'salesPrice', 'purchasePrice'];
const LIST_PRICE = ['listingPrice', 'initialListingPrice'];
// Any-stage deal value: sale price if present, else list price.
const TOP_DEAL_PRICE = [...PRICE, ...LIST_PRICE];
// Commission % is keyed by side, and the generic `commissionPercentage` is set
// on almost nothing — put the side-specific keys ahead of it.
const LISTING_PCT = [
  'listingAgencyCommission',
  'listingBrokerCommissionPercentage',
  'listingBrokerCompensationPercentage',
  'commissionPercentage',
];
const BUYER_PCT = [
  'buyersAgencyCommission',
  'purchaseCompensationPercentage',
  'commissionPercentage',
];
const ANY_PCT = [...LISTING_PCT, ...BUYER_PCT].filter(
  (key, i, all) => all.indexOf(key) === i
);
// Listing start date: `listingDate` exists on some partners' schemas, the
// agreement start date on others. Exported — the report's date-field filter
// resolves the same chains server-side.
export const LIST_DATE = ['listingAgreementStartDate', 'listingDate'];
export const ACCEPTANCE_DATE = ['acceptanceDate', 'mutualAcceptanceDate'];
// Deals that reached contract — everything from Under Contract onward.
const UNDER_CONTRACT_ONWARD = [
  'UNDER_CONTRACT',
  'CLOSING',
  'CLOSED',
  'CLOSED_FILE_COMPLETE',
];
// "Closed" in the business sense; CLOSED_FILE_COMPLETE alone is the terminal
// compliance state and most closed deals never reach it.
const CLOSED_MILESTONES = ['CLOSED', 'CLOSED_FILE_COMPLETE'];
const COMMON: ReportColumn[] = [
  { label: 'Transaction', base: 'name' },
  { label: 'Agent(s)', base: 'agents' },
  { label: 'Office', base: 'office' },
];

// Off by default: the only column that costs an extra join.
const DOCUMENT_ERRORS: ReportColumn = {
  label: 'Document Errors',
  base: 'documentErrors',
  defaultHidden: true,
};

// WA key first, then the ID equivalent. `expirationDate`/`offerExpirationDate`
// are excluded — in both schemas those are the offer expiry, not the listing's.
const TOMLINSON_PRICE = ['purchasePriceGross'];
const TOMLINSON_TOP_DEAL_PRICE = [...TOMLINSON_PRICE, 'listingPrice'];
// Per-side compensation keys. The combined-total key (`commissionPercentage`)
// is gone — the sales form no longer writes it and WA.js no longer defines
// it — so "Commission %" is now list-side% + buy-side% summed, same as the
// old combined value (e.g. 3% + 3% = 6%). No Idaho list-side key exists
// (ID.js has none), so Idaho listings sum to just their buy-side value.
const TOMLINSON_LIST_PCT = 'sellersBrokerageCompensationPercentagePrimary';
const TOMLINSON_BUY_PCT = 'buyersBrokerageCompensationPercentagePrimary';
const TOMLINSON_PCT_SUM = [TOMLINSON_LIST_PCT, TOMLINSON_BUY_PCT];
const TOMLINSON_LIST_DATE = [
  'listDate',
  'listingEffectiveDate',
  'listingAgreementStartDate',
];
const TOMLINSON_LISTING_EXPIRATION = [
  'listingExpirationDate',
  'listingAgreementExpirationDate',
];
// GCI sums Tomlinson's own side(s) of the commission (list-side + buy-side),
// not the market total — a deal where only one side is represented contributes
// only that side. Idaho deals still contribute 0 (ID.js has neither SOT key).
const TOMLINSON_VOLUME_TOTALS: ReportTotal[] = [
  { label: 'Sales Volume', sum: TOMLINSON_PRICE },
  {
    label: 'GCI',
    gci: { price: TOMLINSON_PRICE, pct: TOMLINSON_PCT_SUM, pctMode: 'sum' },
  },
];

export const DEFAULT_TRANSACTION_REPORT_TABS: ReportTab[] = [
  {
    key: 'listings',
    label: 'Listings',
    filters: { representationTypes: ['SELLER', 'BOTH'] },
    columns: [
      ...COMMON,
      { label: 'Milestone', base: 'milestone' },
      {
        label: 'Listing Date',
        sot: LIST_DATE,
        format: 'date',
        flagWhenMissing: true,
      },
      {
        label: 'Expiration',
        sot: [
          'listingAgreementExpirationDate',
          'listingExpirationDate',
          'expirationDate',
        ],
        format: 'date',
      },
      { label: 'List Price', sot: LIST_PRICE, format: 'currency' },
      { label: 'Commission %', sot: LISTING_PCT, format: 'percent' },
      DOCUMENT_ERRORS,
    ],
    totals: [{ label: 'Total List Volume', sum: LIST_PRICE }],
  },
  {
    key: 'contracts',
    label: 'Contracts',
    filters: {
      representationTypes: ['BUYER', 'BOTH'],
      milestones: UNDER_CONTRACT_ONWARD,
    },
    columns: [
      ...COMMON,
      { label: 'Milestone', base: 'milestone' },
      {
        label: 'Acceptance Date',
        sot: ACCEPTANCE_DATE,
        format: 'date',
        flagWhenMissing: true,
      },
      { label: 'Closing Date', base: 'closingDate', format: 'date' },
      { label: 'Purchase Price', sot: PRICE, format: 'currency' },
      { label: 'Commission %', sot: BUYER_PCT, format: 'percent' },
      DOCUMENT_ERRORS,
    ],
    totals: [
      { label: 'Sales Volume', sum: PRICE },
      { label: 'GCI', gci: { price: PRICE, pct: BUYER_PCT } },
    ],
  },
  {
    key: 'closings',
    label: 'Closings',
    filters: { milestones: CLOSED_MILESTONES },
    columns: [
      ...COMMON,
      {
        label: 'Closing Date',
        base: 'closingDate',
        format: 'date',
        flagWhenMissing: true,
      },
      { label: 'Sale Price', sot: PRICE, format: 'currency' },
      { label: 'Commission %', sot: ANY_PCT, format: 'percent' },
      DOCUMENT_ERRORS,
    ],
    totals: [
      { label: 'Sales Volume', sum: PRICE },
      { label: 'GCI', gci: { price: PRICE, pct: ANY_PCT } },
    ],
  },
  // Agents ranked by summed deal value; only this tab shows the "minimum $" control.
  {
    key: 'top-deals',
    label: 'Top Agents',
    variant: 'agentLeaderboard',
    // Used only for the async export fallback (the leaderboard UI builds its own).
    columns: [
      ...COMMON,
      { label: 'Milestone', base: 'milestone' },
      { label: 'Price', sot: TOP_DEAL_PRICE, format: 'currency' },
    ],
    amountField: TOP_DEAL_PRICE,
    defaultMinAmount: 1_000_000,
  },
  // Closed volume/GCI per period vs the period before it. `columns` is unused by
  // this variant — the view is driven entirely by `totals`.
  {
    key: 'comparison',
    label: 'Comparison',
    variant: 'comparison',
    filters: { milestones: CLOSED_MILESTONES },
    columns: [],
    totals: [
      { label: 'Sales Volume', sum: PRICE },
      { label: 'GCI', gci: { price: PRICE, pct: ANY_PCT } },
    ],
    defaultGrain: 'month',
  },
];

// Off by default — partners opt in via `transactionReport: { enabled: true }`.
export const DEFAULT_TRANSACTION_REPORT: TransactionReportConfig = {
  enabled: false,
  tabs: DEFAULT_TRANSACTION_REPORT_TABS,
};

// Off by default — partners opt in via `incompleteCompliance: { enabled: true }`.
export const DEFAULT_INCOMPLETE_COMPLIANCE: IncompleteComplianceConfig = {
  enabled: false,
  closingWithinDays: 7,
  statuses: INCOMPLETE_COMPLIANCE_DOC_STATUSES,
  includeMissingDocs: true,
  columns: [
    ...COMMON,
    { label: 'Milestone', base: 'milestone' },
    { label: 'Closing Date', base: 'closingDate', format: 'date' },
  ],
};

// Listing-side fields: seller/dual deals, up to Pre-Contract.
const LISTING_SIDE_PRE_CONTRACT: ExtraSOTField['renderCondition'] = {
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
  ],
  maxMilestone: 'PRE_CONTRACT',
};

// Sale-side fields: Under Contract or later.
const SALE_SIDE_UNDER_CONTRACT: ExtraSOTField['renderCondition'] = {
  minMilestone: 'UNDER_CONTRACT',
};

const DEFAULT_CONFIG: PartnerConfig = {
  transactionReport: DEFAULT_TRANSACTION_REPORT,
  incompleteCompliance: DEFAULT_INCOMPLETE_COMPLIANCE,
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  // Common date/price fields; a partner's own key/alias overrides these.
  extraSOTFields: [
    {
      fieldKey: 'listingAgreementBeginDate',
      aliasKeys: ['listingAgreementStartDate', 'listingEffectiveDate'],
      renderCondition: LISTING_SIDE_PRE_CONTRACT,
      hideWhenEmpty: true,
    },
    {
      fieldKey: 'listingAgreementExpirationDate',
      aliasKeys: ['listingExpirationDate'],
      renderCondition: LISTING_SIDE_PRE_CONTRACT,
      hideWhenEmpty: true,
    },
    {
      fieldKey: 'listingPrice',
      renderCondition: LISTING_SIDE_PRE_CONTRACT,
      hideWhenEmpty: true,
    },
    {
      fieldKey: 'acceptanceDate',
      renderCondition: SALE_SIDE_UNDER_CONTRACT,
      hideWhenEmpty: true,
    },
    {
      fieldKey: 'closingDate',
      renderCondition: SALE_SIDE_UNDER_CONTRACT,
      hideWhenEmpty: true,
    },
    {
      fieldKey: 'purchasePriceGross',
      renderCondition: SALE_SIDE_UNDER_CONTRACT,
      hideWhenEmpty: true,
    },
  ],
  // All partners: internal Admins only. BW overrides with its legacy bucket
  // view + allowlisted emails — the only partner with the simplified middle
  // ground between simple doc→folder and the full v2 editor.
  folderConfigUI: {
    view: 'v2',
    roles: [ROLES.ADMIN],
    emails: [],
  },
  syncLinkedDocumentStatus: true,
  syncLinkedSotFields: false,
  dualAgencyFilterLabel: 'Dual Agency Only',
  showFinancingTypeField: false,
  showReferringFirmField: false,
  enableInhouseSigning: false,
  allowSigningWithComplianceErrors: true,
  enableDailyHubV2: false,
  enableDocumentChecklist: false,
  enableAskMax: false,
  showAgentTeamField: false,
  showProfitPowerFields: false,
  canEditTransactionAddress: [
    ROLES.ADMIN,
    ROLES.AGENT,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
  timezone: 'America/New_York',
  showComplianceCheck: [],
  showFolderView: [],
  enableFolderViewCheckboxSelection: [
    ROLES.AGENT,
    ROLES.AGENT_ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
    ROLES.ADMIN,
  ],
  searchAndUpdateUser: [ROLES.ADMIN],
  showMlsNumberField: false,
  externalSourceLink: { show: true, label: 'External Source' },
  showTemplateBadgeInTaskView: [ROLES.ADMIN],
  canDeleteTags: [ROLES.ADMIN],
  canRemoveSOTValue: [ROLES.ADMIN],
  canViewSOTHistory: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.AGENT],
  canEditTaskEmailContent: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
  ],
  canCreateComplianceTransaction: [ROLES.ADMIN],
  excludedDocumentStatusesInComplianceNavigation: ['PROCESSING'],
  complianceLoginRedirect: AppURLs.complianceDashboardTransactions(),
  closingMemberLoginRedirect: AppURLs.complianceDashboardTransactions(),
  canRetriggerTasks: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  showClosingTeamQuickFilters: false,
  taskConfigs: {
    addTaskInTransaction: [ROLES.ADMIN],
  },
  allowedSigningOrders: ['preserved', 'random'],
  splitListingMilestones: true,
  excludedMilestonesByRepresentationType: {
    [TRANSACTION_REPRESENTATION_TYPES.BUYER]: ['PRE_LISTING', 'ACTIVE_LISTING'],
  },
};

// Admin-only DailyHub, injected into every partner that lacks its own DailyHub.
// Meant to eventually adopt COMPLIANCE_DAILY_HUB_MODULE's roles (see below).
const DEFAULT_DAILY_HUB_MODULE: ModuleConfig = {
  id: MODULE_IDS.DAILY_HUB,
  enabled: true,
  roles: [ROLES.ADMIN],
  children: [
    {
      id: MODULE_IDS.DAILY_HUB_ADMIN_HUB,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.DAILY_HUB_TEAM_HUB,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.DAILY_HUB_MY_HUB,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
  ],
};

// Richer DailyHub for Compliance/Compliance-Admin/Viewer, added to moduleConfigs
// by partners on the full experience (RW Towne, Arc, Watson, Capstone).
const COMPLIANCE_DAILY_HUB_MODULE: ModuleConfig = {
  id: MODULE_IDS.DAILY_HUB,
  enabled: true,
  roles: [ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
  children: [
    {
      id: MODULE_IDS.DAILY_HUB_ADMIN_HUB,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.DAILY_HUB_TEAM_HUB,
      enabled: true,
      roles: [ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.DAILY_HUB_MY_HUB,
      enabled: true,
      roles: [ROLES.COMPLIANCE, ROLES.VIEWER],
    },
  ],
};

const JOHN_R_WOOD_CONFIG: PartnerConfig = {
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/New_York',
  enableComplianceDocumentUpload: true,
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  externalSourceLink: {
    show: true,
    label: "Christie's Deal ID",
    urlTemplate:
      'https://johnrwood.mychristiesre.com/dms/deal-folders/{externalSourceID}/documents',
    variantsBySetBy: {
      [ExternalSourceIDSetBy.CHRISTIE_PROPERTY_FOLDER]: {
        label: "Christie's Property ID",
        urlTemplate:
          'https://johnrwood.mychristiesre.com/dms/property-folders/{externalSourceID}/documents',
      },
    },
  },
  externalSourceResync: true,
  extraSOTFields: [
    {
      fieldKey: 'mlsNumber',
      label: 'MLS Number',
      type: 'string',
      copyable: true,
    },
  ],
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
  ],
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.CONDO,
    TRANSACTION_PROPERTY_TYPES.LAND_LOT,
    TRANSACTION_PROPERTY_TYPES.NEW_CONSTRUCTION,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OUTBOUND_REFERRAL,
    TRANSACTION_DEAL_TYPES.OUTBOUND_RELOCATION,
  ],
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
};

const REAL_ESTATE_ONE_CONFIG: PartnerConfig = {
  enableAskMax: true,
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/New_York',
  enableComplianceDocumentUpload: true,
  enableInhouseSigning: true,
  showTransactionLinkInAgentEmails: false,
  enablePayments: true,
  showComplianceCheck: [ROLES.AGENT],
  showAgentReporting: [ROLES.ADMIN],
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  externalSourceLink: { show: false, label: 'Deal Number' },
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
  ],
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.OTHER,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
    TRANSACTION_REPRESENTATION_TYPES.OTHER,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OTHER,
  ],
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
  agreementForms: [
    {
      key: 'real-estate-one-buyer-v1',
      label: 'Buyer Agreement',
      path: AppURLs.agentIntake('real-estate-one-buyer-v1'),
    },
    {
      key: 'real-estate-one-listing-v1',
      label: 'Listing Agreement',
      path: AppURLs.agentIntake('real-estate-one-listing-v1'),
    },
    {
      key: 'real-estate-one-tenant-rep-v1',
      label: 'Tenant Agreement',
      path: AppURLs.agentIntake('real-estate-one-tenant-rep-v1'),
    },
    {
      key: 'real-estate-one-rental-v1',
      label: 'Rental Listing Agreement',
      path: AppURLs.agentIntake('real-estate-one-rental-v1'),
    },
  ],
  offerFormId: 'real-estate-one-offer-v1',
};

const COASTAL_CONFIG: PartnerConfig = {
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/New_York',
  enableComplianceDocumentUpload: true,
  enableFallThroughWorkflow: true,
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  externalSourceLink: { show: true, label: 'Paperless Link' },
  externalSourceResync: true,
  dualAgencyFilterLabel: 'Double-side Transaction',
  showAgentTeamField: true,
  // Uses the default report tabs; set `tabs` here to tailor columns/labels.
  transactionReport: {
    enabled: true,
    tabs: [
      ...DEFAULT_TRANSACTION_REPORT_TABS,
      {
        key: 'missing-bba',
        label: 'Missing BBA',
        filters: {
          representationTypes: ['BUYER', 'BOTH'],
          missingDocTypes: [
            'FL_CO_EXBUBA', // Exclusive Buyer's Brokerage Agreement
            'FL_CO_SABBANE', // Showing Agreement / Buyer Broker Agreement (Non-Exclusive)
            'FL_EBBA', // Exclusive Buyer Brokerage Agreement
            'FL_EBBASA', // Exclusive Buyer Brokerage Agreement - Single Agent
            'FL_EBBATB', // Exclusive Buyer Brokerage Agreement - Transaction Broker
            'FL_EBBATTB', // Exclusive BBA - Consent to Transition to Transaction Broker
            'FL_BBAPS', // Buyer Broker Agreement (Property-Specific)
            'FL_BUBRAGE', // Buyer Broker Agreement (Exclusive)
            'FL_BUBREA', // Buyer-Broker Exclusive Agreement
          ],
        },
        columns: [
          ...COMMON,
          { label: 'Milestone', base: 'milestone' },
          {
            label: 'Acceptance Date',
            sot: ['acceptanceDate', 'mutualAcceptanceDate'],
            format: 'date',
          },
        ],
      },
    ],
  },
  incompleteCompliance: { enabled: true },
  extraSOTFields: [
    {
      fieldKey: 'mlsNumber',
      label: 'MLS Number',
      type: 'string',
      copyable: true,
    },
    { fieldKey: 'buyer', label: 'Buyer name' },
    { fieldKey: 'seller', label: 'Seller name' },
    { fieldKey: 'escrowAgent', label: 'Title Company', type: 'string' },
    { fieldKey: 'acceptanceDate', label: 'Effective Date', type: 'date' },
    { fieldKey: 'closingDate', label: 'Closing Date', type: 'date' },
    {
      fieldKey: 'listingAgencyCommission',
      type: 'string',
      hideWhenEmpty: true,
    },
    { fieldKey: 'buyersAgencyCommission', type: 'string', hideWhenEmpty: true },
    {
      fieldKey: 'purchaseCompensationPercentage',
      label: 'BBA Compensation %',
      type: 'string',
      hideWhenEmpty: true,
    },
    {
      fieldKey: 'purchaseCompensationAmount',
      label: 'BBA Compensation Amount',
      type: 'string',
      hideWhenEmpty: true,
    },
  ],
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    COMPLIANCE_DAILY_HUB_MODULE,
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.EMAIL_TRIAGE,
      enabled: true,
      roles: [ROLES.ADMIN, ROLES.COMPLIANCE],
    },
    {
      id: MODULE_IDS.COMPLIANCE_REPORTING,
      enabled: true,
      roles: [ROLES.ADMIN, ROLES.COMPLIANCE_ADMIN],
    },
  ],
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.COMMERCIAL,
    TRANSACTION_PROPERTY_TYPES.CONDO,
    TRANSACTION_PROPERTY_TYPES.LAND_LOT,
    TRANSACTION_PROPERTY_TYPES.NEW_CONSTRUCTION,
    TRANSACTION_PROPERTY_TYPES.OTHER,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
    TRANSACTION_REPRESENTATION_TYPES.OTHER,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OTHER,
  ],
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
};

const GARY_GREENE_CONFIG: PartnerConfig = {
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/Chicago',
  enableComplianceDocumentUpload: true,
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  externalSourceLink: {
    show: true,
    label: 'Docusign Rooms',
    urlTemplate: 'https://rooms.docusign.com/rooms/{externalSourceID}/details',
  },
  externalSourceResync: true,
  extraSOTFields: [
    {
      fieldKey: 'dpnNumber',
      label: 'DPN',
      type: 'string',
      copyable: true,
    },
    {
      fieldKey: 'buyer',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'seller',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'buyersDesignatedAgent',
      label: "Buyer's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellersDesignatedAgent',
      label: "Seller's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellingCompany',
      label: "Buyer's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'listingCompany',
      label: "Seller's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    { fieldKey: 'mortgageCompany', label: 'Mortgage Company', type: 'string' },
    { fieldKey: 'loanOfficer', label: 'Loan Officer', type: 'string' },
    {
      fieldKey: 'loanOfficer2',
      label: 'Loan Officer 2',
      type: 'string',
      hideWhenEmpty: true,
    },
    {
      fieldKey: 'loanOfficer3',
      label: 'Loan Officer 3',
      type: 'string',
      hideWhenEmpty: true,
    },
    { fieldKey: 'titleCompany', label: 'Title Company', type: 'string' },
    { fieldKey: 'titleCompanyCloser', label: 'Closer', type: 'string' },
    {
      fieldKey: 'titleCompanyEmail',
      label: 'Title Company Email',
      type: 'list',
    },
    {
      fieldKey: 'titleCompanyPhone',
      label: 'Title Company Phone',
      type: 'string',
    },
  ],
  dualAgencyFilterLabel: 'Intermediary Only',
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    COMPLIANCE_DAILY_HUB_MODULE,
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.COMPLIANCE_REPORTING,
      enabled: true,
      roles: [ROLES.ADMIN, ROLES.COMPLIANCE_ADMIN],
    },
  ],
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.OTHER,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
    TRANSACTION_REPRESENTATION_TYPES.OTHER,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.NEW_CONSTRUCTION,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OTHER,
  ],
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
};

const HELEN_ADAMS_REALTY_CONFIG: PartnerConfig = {
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/New_York',
  enableComplianceDocumentUpload: true,
  showComplianceCheck: [ROLES.AGENT, ROLES.ADMIN],
  enableInhouseSigning: true,
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  defaultDocumentListViewMode: 'list',
  externalSourceLink: { show: true, label: 'Dotloop Link' },
  externalSourceResync: true,
  agreementForms: [
    {
      key: 'helen-adams-buyer-v1',
      label: 'Buyer Agency Agreement',
      path: AppURLs.agentIntake('helen-adams-buyer-v1'),
    },
    {
      key: 'helen-adams-listing-v1',
      label: 'Listing Agreement',
      path: AppURLs.agentIntake('helen-adams-listing-v1'),
    },
  ],
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    COMPLIANCE_DAILY_HUB_MODULE,
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.PARTNER_LOOPS,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.EMAIL_TRIAGE,
      enabled: true,
      roles: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
    },
  ],
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.OTHER,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OTHER,
  ],
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
  // EV-3469: surface the listing expiration and a side-aware Compensation row.
  // List side shows the listing-agreement fee; buy side shows the buyer-agent fee
  // (the SOT prefers the compensation form over the buyer agency agreement).
  extraSOTFields: [
    {
      fieldKey: 'listingAgreementExpirationDate',
      label: 'Expiration Date',
      type: 'date',
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
      },
    },
    {
      fieldKey: 'sellerBrokerCompensationPercentage',
      label: 'Compensation',
      type: 'string',
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
      },
    },
    {
      fieldKey: 'buyerAgentCompensationPercentage',
      label: 'Compensation',
      type: 'string',
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
      },
    },
  ],
};

const KEYES_PARTNER_CONFIG: PartnerConfig = {
  enableAskMax: true,
  splitListingMilestones: false,
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/New_York',
  enableComplianceDocumentUpload: true,
  showComplianceCheck: [ROLES.AGENT, ROLES.ADMIN],
  enableInhouseSigning: true,
  transactionTypeV2Enabled: true,
  preferApiErrorVertices: false,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.COMMERCIAL,
    TRANSACTION_PROPERTY_TYPES.LAND_LOT,
    TRANSACTION_PROPERTY_TYPES.NEW_CONSTRUCTION,
    TRANSACTION_PROPERTY_TYPES.OTHER,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
    TRANSACTION_REPRESENTATION_TYPES.OTHER,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OUTBOUND_REFERRAL,
    TRANSACTION_DEAL_TYPES.BPO,
    TRANSACTION_DEAL_TYPES.APT_RENTAL,
    TRANSACTION_DEAL_TYPES.LEASE_RENEWAL,
    TRANSACTION_DEAL_TYPES.OTHER,
  ],
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE, ROLES.ADMIN],
  defaultDocumentListViewMode: 'list',
  agreementForms: [
    {
      key: 'keyes-buyer-v1',
      label: 'Buyer Agreement',
      path: AppURLs.agentIntake('keyes-buyer-v1'),
    },
    {
      key: 'keyes-listing-v1',
      label: 'Listing Agreement',
      path: AppURLs.agentIntake('keyes-listing-v1'),
    },
    {
      key: 'keyes-lease-offer-v1',
      label: 'Tenant Agreement',
      path: AppURLs.agentIntake('keyes-lease-offer-v1'),
    },
    {
      key: 'keyes-lease-listing-v1',
      label: 'Rental Listing Agreement',
      path: AppURLs.agentIntake('keyes-lease-listing-v1'),
    },
  ],
  externalSourceLink: { show: true, label: 'Deal Number' },
  externalSourceResync: true,
  resyncAvailabilityStrategy: 'linkedTdTransaction',
  extraSOTFields: [
    {
      fieldKey: 'qualiaOrderNumber',
      label: 'HPT Number',
      type: 'string',
      copyable: true,
      renderCondition: { dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE] },
    },
    {
      fieldKey: 'buyer',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'seller',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'tenant',
      renderCondition: { dealTypes: [TRANSACTION_DEAL_TYPES.LEASE] },
    },
    {
      fieldKey: 'landlord',
      renderCondition: { dealTypes: [TRANSACTION_DEAL_TYPES.LEASE] },
    },
    {
      fieldKey: 'buyerAgentName',
      label: "Buyer's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellerAgentName',
      label: "Seller's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'tenantAgent',
      label: "Tenant's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.LEASE],
      },
    },
    {
      fieldKey: 'landlordAgent',
      label: "Landlord's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.LEASE],
      },
    },
    {
      fieldKey: 'cooperatingBroker',
      label: "Buyer's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'listingBroker',
      label: "Seller's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'tenantBrokerage',
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.LEASE],
      },
    },
    {
      fieldKey: 'landlordBrokerage',
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.LEASE],
      },
    },
  ],
  customFilterPresets: [
    {
      id: 'needs_processor_review',
      label: 'Needs Processor Review',
      tooltip: 'Transactions tagged Ready for User Review or Validation Failed',
      filters: {
        selectedTags: [
          TAG_NAMES.READY_FOR_USER_REVIEW,
          TAG_NAMES.VALIDATION_FAILED,
        ],
        tagMatchMode: 'OR',
      },
    },
  ],
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.EFTS,
      enabled: true,
      roles: [ROLES.ADMIN, ROLES.COMPLIANCE],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.COMPLIANCE_REPORTING,
      enabled: true,
      roles: [ROLES.ADMIN, ROLES.COMPLIANCE_ADMIN],
    },
  ],
};

const TOMLINSON_CONFIG: PartnerConfig = {
  showMlsNumberField: true,
  showFinancingTypeField: true,
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/Los_Angeles',
  enableComplianceDocumentUpload: true,
  enableFallThroughWorkflow: true,
  enableDocumentOwnership: true,
  syncLinkedSotFields: true,
  documentOwnerRoles: [
    DOC_OWNER.AGENT,
    DOC_OWNER.PROCESSOR,
    DOC_OWNER.CLOSING_MEMBER,
  ],
  transactionTypeV2Enabled: true,
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.COMMERCIAL,
    TRANSACTION_PROPERTY_TYPES.LAND_LOT,
    TRANSACTION_PROPERTY_TYPES.FARM,
    TRANSACTION_PROPERTY_TYPES.OTHER,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
    TRANSACTION_REPRESENTATION_TYPES.OTHER,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OTHER,
    TRANSACTION_DEAL_TYPES.REFERRAL,
    TRANSACTION_DEAL_TYPES.BPO,
  ],
  showFolderView: [ROLES.COMPLIANCE, ROLES.ADMIN],
  externalSourceLink: { show: false, label: '' },
  // Two SOT schemas with different key names, so the shared
  // DEFAULT_TRANSACTION_REPORT_TABS chains don't resolve here.
  transactionReport: {
    enabled: true,
    tabs: [
      {
        key: 'listings',
        label: 'Listings',
        filters: { representationTypes: ['SELLER'] },
        columns: [
          ...COMMON,
          { label: 'Milestone', base: 'milestone' },
          { label: 'MLS #', sot: ['mlsNumber'] },
          {
            label: 'Listing Date',
            sot: TOMLINSON_LIST_DATE,
            format: 'date',
          },
          {
            label: 'Expiration',
            sot: TOMLINSON_LISTING_EXPIRATION,
            format: 'date',
          },
          // ID.js has no listing-price key; blank on Idaho deals.
          { label: 'List Price', sot: ['listingPrice'], format: 'currency' },
          {
            label: 'Commission %',
            sumSot: TOMLINSON_PCT_SUM,
            format: 'percent',
          },
          DOCUMENT_ERRORS,
        ],
        totals: [{ label: 'Total List Volume', sum: ['listingPrice'] }],
      },
      {
        key: 'contracts',
        label: 'Contracts',
        filters: { representationTypes: ['BUYER'] },
        columns: [
          ...COMMON,
          { label: 'Milestone', base: 'milestone' },
          { label: 'MLS #', sot: ['mlsNumber'] },
          {
            label: 'Acceptance Date',
            sot: ['acceptanceDate'],
            format: 'date',
          },
          { label: 'Closing Date', base: 'closingDate', format: 'date' },
          {
            label: 'Purchase Price',
            sot: TOMLINSON_PRICE,
            format: 'currency',
          },
          {
            label: 'Commission %',
            sumSot: TOMLINSON_PCT_SUM,
            format: 'percent',
          },
          DOCUMENT_ERRORS,
        ],
        totals: TOMLINSON_VOLUME_TOTALS,
      },
      {
        key: 'closings',
        label: 'Closings',
        filters: { milestones: ['CLOSED_FILE_COMPLETE'] },
        columns: [
          ...COMMON,
          { label: 'Closing Date', base: 'closingDate', format: 'date' },
          { label: 'Sale Price', sot: TOMLINSON_PRICE, format: 'currency' },
          {
            label: 'Commission %',
            sumSot: TOMLINSON_PCT_SUM,
            format: 'percent',
          },
          DOCUMENT_ERRORS,
        ],
        totals: TOMLINSON_VOLUME_TOTALS,
      },
      {
        key: 'top-deals',
        label: 'Top Agents',
        variant: 'agentLeaderboard',
        columns: [
          ...COMMON,
          { label: 'Milestone', base: 'milestone' },
          {
            label: 'Price',
            sot: TOMLINSON_TOP_DEAL_PRICE,
            format: 'currency',
          },
        ],
        amountField: TOMLINSON_TOP_DEAL_PRICE,
        defaultMinAmount: 1_000_000,
      },
      {
        key: 'comparison',
        label: 'Comparison',
        variant: 'comparison',
        filters: { milestones: ['CLOSED_FILE_COMPLETE'] },
        columns: [],
        totals: TOMLINSON_VOLUME_TOTALS,
        defaultGrain: 'month',
      },
    ],
  },
  extraSOTFields: [
    {
      fieldKey: 'seller',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'buyer',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'buyersDesignatedAgent',
      label: "Buyer's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellersDesignatedAgent',
      label: "Seller's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'buyersBrokerage',
      label: "Buyer's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellersBrokerage',
      label: "Seller's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'closingDate',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'acceptanceDate',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'listDate',
      aliasKeys: ['listingAgreementStartDate'],
      label: 'Listing Date',
      type: 'date',
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        milestone: 'PRE_CONTRACT',
      },
    },
    {
      fieldKey: 'listingExpirationDate',
      aliasKeys: ['listingAgreementExpirationDate'],
      label: 'Expiration Date',
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        milestone: 'PRE_CONTRACT',
      },
    },
    {
      fieldKey: 'purchasePriceGross',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'listingPrice',
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        milestone: 'PRE_CONTRACT',
      },
    },
    {
      fieldKey: 'inhouseTransactionCoordinator',
      label: 'In-house Transaction Coordinator',
      widget: {
        kind: 'radio' as const,
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
    },
    {
      fieldKey: 'earnestMoneyDueDate',
      label: 'Earnest Money Date',
      type: 'date',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'earnestMoneyDeposit',
      label: 'Earnest Money Amount',
      type: 'string',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'homeInspectionContingency',
      label: 'Home Inspection Contingency',
      widget: {
        kind: 'radio' as const,
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'homeInspectionDate',
      aliasKeys: ['inspectionDueDate'],
      label: 'Home Inspection Date',
      type: 'date',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'homeInspectionTimeFrame',
      aliasKeys: ['inspectionCompletionDays'],
      label: 'Home Inspection Time Frame',
      type: 'string',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'closingAgentCompany',
      label: 'Closing Agent (Company)',
      type: 'string',
      copyable: true,
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'closingAgentIndividual',
      label: 'Closing Agent (Individual)',
      type: 'string',
      copyable: true,
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'closingAgentEmail',
      label: 'Closing Agent Email',
      type: 'string',
      copyable: true,
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'commissionBasedOn',
      label: 'Amount Commission Based On',
      type: 'string',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'sellersBrokerageCompensationPercentagePrimary',
      aliasKeys: ['sellingCommissionPercentage'],
      label: 'Listing Firm Compensation Percentage – Primary Agreement',
      type: 'string',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'buyersBrokerageCompensationPercentagePrimary',
      aliasKeys: ['buyersBrokerageCompensationPercentage'],
      label: 'Buying Firm Compensation Percentage - Primary Agreement',
      type: 'string',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'referral',
      label: 'Referral',
      widget: {
        kind: 'radio' as const,
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
    },
    {
      fieldKey: 'referralNotes',
      label: 'Referral Notes',
      type: 'string',
    },
    {
      fieldKey: 'listSideReferralAmount',
      label: 'List Side Referral Amount',
      type: 'string',
    },
  ],
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    COMPLIANCE_DAILY_HUB_MODULE,
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.COMPLIANCE_REPORTING,
      enabled: true,
      roles: [ROLES.ADMIN, ROLES.COMPLIANCE_ADMIN],
    },
  ],
};

const BAIRD_AND_WARNER_PARTNER_CONFIG: PartnerConfig = {
  enableInhouseSigning: true,
  // The only offer contracts configured for BW are the Illinois purchase
  // ones, so a rental would draft a purchase agreement.
  leaseOfferDrafting: false,
  allowedSigningOrders: ['preserved'],
  emailProcessorReviewWithoutCompliance: true,
  showTransactionLinkInAgentEmails: true,
  externalSourceLink: { show: true, label: 'Dotloop Link' },
  excludedMilestonesByTransactionType: {
    PURCHASE: ['CLOSED_FILE_COMPLETE'],
    LISTING: ['CLOSED_FILE_COMPLETE'],
    LEASE_LISTING: ['CLOSED_FILE_COMPLETE', 'CLOSING'],
    LEASE_OFFER: ['CLOSED_FILE_COMPLETE', 'CLOSING'],
  },
  folderConfigUI: {
    view: 'legacy',
    roles: [ROLES.ADMIN],
    emails: [
      'bill.mastbrook@bairdwarner.com',
      'lori.jones@bairdwarner.com',
      'jan.koch@bairdwarner.com',
      'sherry.gesiorski@bairdwarner.com',
      'stephanie.scudder@bairdwarner.com',
    ],
  },
  showMlsNumberField: true,
  canEditClosingDate: [ROLES.ADMIN],
  showProfitPowerFields: true,
  showTransactionTileMetadata: true,
  timezone: 'America/Chicago',
  showComplianceCheck: parseShowComplianceCheckEnv(
    import.meta.env.VITE_SHOW_COMPLIANCE_CHECK
  ),
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  complianceLoginRedirect: AppURLs.complianceDashboardChecks(),
  closingMemberLoginRedirect: AppURLs.complianceDashboardEarnestly(),
  extraSOTFields: [
    {
      fieldKey: 'buyer',
      renderCondition: { transactionTypes: ['LISTING', 'PURCHASE'] },
    },
    {
      fieldKey: 'seller',
      renderCondition: { transactionTypes: ['LISTING', 'PURCHASE'] },
    },
    {
      fieldKey: 'tenant',
      renderCondition: { transactionTypes: ['LEASE_LISTING', 'LEASE_OFFER'] },
    },
    {
      fieldKey: 'landlord',
      renderCondition: { transactionTypes: ['LEASE_LISTING', 'LEASE_OFFER'] },
    },
    {
      fieldKey: 'buyersDesignatedAgent',
      label: "Buyer's Agent",
      renderCondition: { transactionTypes: ['LISTING'] },
    },
    {
      fieldKey: 'sellersDesignatedAgent',
      label: "Seller's Agent",
      renderCondition: { transactionTypes: ['PURCHASE'] },
    },
    {
      fieldKey: 'tenantDesignatedAgent',
      label: "Tenant's Agent",
      renderCondition: { transactionTypes: ['LEASE_LISTING'] },
    },
    {
      fieldKey: 'landlordDesignatedAgent',
      label: "Landlord's Agent",
      renderCondition: { transactionTypes: ['LEASE_OFFER'] },
    },
    {
      fieldKey: 'buyersBrokerage',
      label: "Buyer's Brokerage",
      renderCondition: { transactionTypes: ['LISTING'] },
    },
    {
      fieldKey: 'sellersBrokerage',
      label: "Seller's Brokerage",
      renderCondition: { transactionTypes: ['PURCHASE'] },
    },
    {
      fieldKey: 'landlordBrokerage',
      renderCondition: { transactionTypes: ['LEASE_OFFER'] },
    },
  ],
  externalSourceResync: true,
  moduleConfigs: [
    {
      id: MODULE_IDS.CHECKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.COMPLIANCE_TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.EARNESTLY,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.FORMS_LIBRARY,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
  ],
  agreementForms: [
    {
      key: 'bw-buyer-agreement',
      label: 'Buyer Agreement',
      path: AppURLs.agentDashboardBuyerOffer(),
    },
    {
      key: 'bw-listing-agreement',
      label: 'Listing Agreement',
      path: AppURLs.agentDashboardListingOffer(),
    },
    {
      key: 'bw-tenant-agreement',
      label: 'Tenant Agreement',
      path: AppURLs.agentDashboardLeaseOffer(),
    },
    {
      key: 'bw-rental-listing-agreement',
      label: 'Rental Listing Agreement',
      path: AppURLs.agentDashboardLeaseListingOffer(),
    },
  ],
};

const ARC_REALTY_CONFIG: PartnerConfig = {
  // The brokerage these are being tested against. Both stay off for everyone else until each has
  // been through testing on its own.
  enableDailyHubV2: true,
  enableDocumentChecklist: true,
  showAllTransactionsTabToRoles: [ROLES.COMPLIANCE_ADMIN],
  showMlsNumberField: true,
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  enableDocumentOwnership: true,
  documentOwnerRoles: [
    DOC_OWNER.AGENT,
    DOC_OWNER.PROCESSOR,
    DOC_OWNER.CLOSING_MEMBER,
  ],
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
  timezone: 'America/Chicago',
  enableComplianceDocumentUpload: true,
  showClosingTeamQuickFilters: true,
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  externalSourceLink: { show: true, label: 'Paperless Link' },
  externalSourceResync: true,
  transactionTypeV2Enabled: true,
  syncLinkedDocumentStatus: true,
  syncLinkedSotFields: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.COMMERCIAL,
    TRANSACTION_PROPERTY_TYPES.LAND_LOT,
    TRANSACTION_PROPERTY_TYPES.LOT_RESERVATION,
    TRANSACTION_PROPERTY_TYPES.NEW_CONSTRUCTION,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OUTBOUND_REFERRAL,
    TRANSACTION_DEAL_TYPES.REFERRAL,
    TRANSACTION_DEAL_TYPES.FSBO,
    TRANSACTION_DEAL_TYPES.OUTBOUND_RELOCATION,
  ],
  extraSOTFields: [
    {
      fieldKey: 'buyer',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'seller',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'buyersDesignatedAgent',
      label: "Buyer's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellersDesignatedAgent',
      label: "Seller's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellingCompany',
      label: "Buyer's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'listingCompany',
      label: "Seller's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
  ],
  customFilterPresets: [
    {
      id: 'closing_team_ready',
      label: 'Closing Team Ready',
      tooltip: 'Closed transactions with no missing or rejected documents',
      filters: {
        selectedTransactionMilestones: ['CLOSED'],
        excludeTags: [
          TAG_NAMES.MISSING_DOCS_AGENT,
          TAG_NAMES.MISSING_DOCS_PROCESSOR,
          TAG_NAMES.DOCUMENT_REJECTED_SENT_TO_AGENT,
        ],
      },
    },
    {
      id: 'closed_incomplete',
      label: 'Closed - Incomplete',
      tooltip: 'Closed transactions with missing or rejected documents',
      filters: {
        selectedTransactionMilestones: ['CLOSED'],
        selectedTags: [
          TAG_NAMES.MISSING_DOCS_AGENT,
          TAG_NAMES.MISSING_DOCS_PROCESSOR,
          TAG_NAMES.DOCUMENT_REJECTED_SENT_TO_AGENT,
        ],
        tagMatchMode: 'OR',
      },
    },
  ],
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    COMPLIANCE_DAILY_HUB_MODULE,
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
  ],
  dailyHubRoleSlots: { PROCESSING_TEAM: true, CLOSING_TEAM: true },
};

const CAPSTONE_CONFIG: PartnerConfig = {
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  enableDocumentOwnership: true,
  documentOwnerRoles: [
    DOC_OWNER.AGENT,
    DOC_OWNER.PROCESSOR,
    DOC_OWNER.CLOSING_MEMBER,
  ],
  syncLinkedSotFields: true,
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
  timezone: 'America/Chicago',
  enableComplianceDocumentUpload: true,
  showClosingTeamQuickFilters: true,
  showFolderView: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  externalSourceLink: { show: true, label: 'Paperless Link' },
  externalSourceResync: true,
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.COMMERCIAL,
    TRANSACTION_PROPERTY_TYPES.LAND_LOT,
    TRANSACTION_PROPERTY_TYPES.LOT_RESERVATION,
    TRANSACTION_PROPERTY_TYPES.NEW_CONSTRUCTION,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OUTBOUND_REFERRAL,
    TRANSACTION_DEAL_TYPES.REFERRAL,
    TRANSACTION_DEAL_TYPES.FSBO,
    TRANSACTION_DEAL_TYPES.OUTBOUND_RELOCATION,
  ],
  extraSOTFields: [
    {
      fieldKey: 'buyer',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'seller',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'buyersDesignatedAgent',
      label: "Buyer's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellersDesignatedAgent',
      label: "Seller's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellingCompany',
      label: "Buyer's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'listingCompany',
      label: "Seller's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
  ],
  customFilterPresets: [
    {
      id: 'closing_team_ready',
      label: 'Closing Team Ready',
      tooltip: 'Closed transactions with no missing docs',
      filters: {
        selectedTransactionMilestones: ['CLOSED'],
        excludeTags: [
          TAG_NAMES.MISSING_DOCS_AGENT,
          TAG_NAMES.MISSING_DOCS_PROCESSOR,
        ],
      },
    },
    {
      id: 'closed_incomplete',
      label: 'Closed - Incomplete',
      tooltip: 'Closed transactions with missing docs',
      filters: {
        selectedTransactionMilestones: ['CLOSED'],
        selectedTags: [
          TAG_NAMES.MISSING_DOCS_AGENT,
          TAG_NAMES.MISSING_DOCS_PROCESSOR,
        ],
        tagMatchMode: 'OR',
      },
    },
  ],
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    COMPLIANCE_DAILY_HUB_MODULE,
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
  ],
  dailyHubRoleSlots: { PROCESSING_TEAM: true, CLOSING_TEAM: true },
};

const RW_TOWNE_CONFIG: PartnerConfig = {
  externalSourceLink: { show: true, label: 'Dotloop Link' },
  externalSourceResync: true,
  showAllTransactionsTabToRoles: [ROLES.COMPLIANCE_ADMIN],
  showReferringFirmField: true,
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  canCreateComplianceTransaction: [ROLES.ADMIN, ROLES.COMPLIANCE_ADMIN],
  enableDocumentOwnership: true,
  documentOwnerRoles: [
    DOC_OWNER.AGENT,
    DOC_OWNER.PROCESSOR,
    DOC_OWNER.CLOSING_MEMBER,
  ],
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE, ROLES.VIEWER],
  timezone: 'America/New_York',
  enableComplianceDocumentUpload: true,
  enableFallThroughWorkflow: true,
  enablePurchaseFallThroughWorkflow: true,
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.COMMERCIAL,
    TRANSACTION_PROPERTY_TYPES.LAND_LOT,
    TRANSACTION_PROPERTY_TYPES.NEW_CONSTRUCTION,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OUTBOUND_REFERRAL,
  ],
  extraSOTFields: [
    {
      fieldKey: 'buyer',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'seller',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'buyersDesignatedAgent',
      label: "Buyer's Agent",
      type: 'list',
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellersDesignatedAgent',
      label: "Seller's Agent",
      type: 'list',
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'buyersBrokerage',
      label: "Buyer's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellersBrokerage',
      label: "Seller's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'propertyMLSNumber',
      label: 'MLS Number',
    },
  ],
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    COMPLIANCE_DAILY_HUB_MODULE,
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.EMAIL_TRIAGE,
      enabled: true,
      roles: [ROLES.ADMIN, ROLES.COMPLIANCE_ADMIN],
    },
  ],
  taskConfigs: {
    addTaskInTransaction: [
      ROLES.ADMIN,
      ROLES.COMPLIANCE,
      ROLES.COMPLIANCE_ADMIN,
    ],
  },
  dailyHubDefaultPageSize: 20,
};

const CUTLER_PARTNER_CONFIG: PartnerConfig = {
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/New_York',
  enableComplianceDocumentUpload: true,
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  externalSourceLink: { show: true, label: 'Source Link' },
  externalSourceResync: true,
  showAccountingID: true,
  earnestMoneyHeldIndicator: {
    holderMatch: 'Cutler',
    label: 'Earnest Money Held by Cutler',
  },
  extraSOTFields: [
    {
      fieldKey: 'buyer',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'seller',
      renderCondition: {
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'buyersDesignatedAgent',
      label: "Buyer's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellersDesignatedAgent',
      label: "Seller's Agent",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'sellingCompany',
      label: "Buyer's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'listingCompany',
      label: "Seller's Brokerage",
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.BUYER],
        dealTypes: [TRANSACTION_DEAL_TYPES.PURCHASE],
      },
    },
    {
      fieldKey: 'propertyMLSNumber',
      label: 'MLS Number',
    },
  ],
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    COMPLIANCE_DAILY_HUB_MODULE,
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.PARTNER_LOOPS,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.COMPLIANCE_REPORTING,
      enabled: true,
      roles: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
    },
  ],
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.OTHER,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OTHER,
  ],
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
};

const WATSON_CONFIG: PartnerConfig = {
  showBoardColumn: true,
  showMlsNumberField: true,
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/New_York',
  enableComplianceDocumentUpload: true,
  enableFallThroughWorkflow: true,
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  // Uses the default report tabs; set `tabs` here to tailor columns/labels.
  transactionReport: {
    enabled: true,
    tabs: [
      ...DEFAULT_TRANSACTION_REPORT_TABS,
      {
        key: 'missing-bba',
        label: 'Missing BBA',
        filters: {
          representationTypes: ['BUYER', 'BOTH'],
          missingDocTypes: [
            'FL_EBBA', // Exclusive Buyer Brokerage Agreement
            'FL_EBBASA', // Exclusive Buyer Brokerage Agreement - Single Agent
            'FL_EBBATB', // Exclusive Buyer Brokerage Agreement - Transaction Broker
            'FL_EBBATTB', // Exclusive BBA - Consent to Transition to Transaction Broker
            'FL_SABBANE', // Showing Agreement / Buyer Broker Agreement (Non-Exclusive)
            'FL_BBAPS', // Buyer Broker Agreement (Property-Specific)
            'FL_BUBRAGE', // Buyer Broker Agreement (Exclusive)
            'FL_BUBREA', // Buyer-Broker Exclusive Agreement
          ],
        },
        columns: [
          ...COMMON,
          { label: 'Milestone', base: 'milestone' },
          {
            label: 'Acceptance Date',
            sot: ['acceptanceDate', 'mutualAcceptanceDate'],
            format: 'date',
          },
        ],
      },
    ],
  },
  incompleteCompliance: { enabled: true },
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.CONDO,
    TRANSACTION_PROPERTY_TYPES.LAND_LOT,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.PURCHASE_AS_IS,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.NEW_CONSTRUCTION,
  ],
  externalSourceLink: { show: true, label: 'Skyslope Link' },
  externalSourceResync: true,
  extraSOTFields: [
    {
      fieldKey: 'buyer',
      renderCondition: {
        dealTypes: [
          TRANSACTION_DEAL_TYPES.PURCHASE,
          TRANSACTION_DEAL_TYPES.PURCHASE_AS_IS,
          TRANSACTION_DEAL_TYPES.NEW_CONSTRUCTION,
        ],
      },
    },
    {
      fieldKey: 'seller',
      renderCondition: {
        dealTypes: [
          TRANSACTION_DEAL_TYPES.PURCHASE,
          TRANSACTION_DEAL_TYPES.PURCHASE_AS_IS,
          TRANSACTION_DEAL_TYPES.NEW_CONSTRUCTION,
        ],
      },
    },
    {
      fieldKey: 'buyerAgentName',
      label: "Buyer's Agent",
      renderCondition: {
        representationTypes: [
          TRANSACTION_REPRESENTATION_TYPES.SELLER,
          TRANSACTION_REPRESENTATION_TYPES.BOTH,
        ],
        dealTypes: [
          TRANSACTION_DEAL_TYPES.PURCHASE,
          TRANSACTION_DEAL_TYPES.PURCHASE_AS_IS,
          TRANSACTION_DEAL_TYPES.NEW_CONSTRUCTION,
        ],
      },
    },
    {
      fieldKey: 'sellerAgentName',
      label: "Seller's Agent",
      renderCondition: {
        representationTypes: [
          TRANSACTION_REPRESENTATION_TYPES.BUYER,
          TRANSACTION_REPRESENTATION_TYPES.BOTH,
        ],
        dealTypes: [
          TRANSACTION_DEAL_TYPES.PURCHASE,
          TRANSACTION_DEAL_TYPES.PURCHASE_AS_IS,
          TRANSACTION_DEAL_TYPES.NEW_CONSTRUCTION,
        ],
      },
    },
    {
      fieldKey: 'cooperatingBroker',
      label: "Buyer's Brokerage",
      renderCondition: {
        representationTypes: [
          TRANSACTION_REPRESENTATION_TYPES.SELLER,
          TRANSACTION_REPRESENTATION_TYPES.BOTH,
        ],
        dealTypes: [
          TRANSACTION_DEAL_TYPES.PURCHASE,
          TRANSACTION_DEAL_TYPES.PURCHASE_AS_IS,
          TRANSACTION_DEAL_TYPES.NEW_CONSTRUCTION,
        ],
      },
    },
    {
      fieldKey: 'listingBroker',
      label: "Seller's Brokerage",
      renderCondition: {
        representationTypes: [
          TRANSACTION_REPRESENTATION_TYPES.BUYER,
          TRANSACTION_REPRESENTATION_TYPES.BOTH,
        ],
        dealTypes: [
          TRANSACTION_DEAL_TYPES.PURCHASE,
          TRANSACTION_DEAL_TYPES.PURCHASE_AS_IS,
          TRANSACTION_DEAL_TYPES.NEW_CONSTRUCTION,
        ],
      },
    },
    {
      fieldKey: 'purchasePriceGross',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'acceptanceDate',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'closingDate',
      renderCondition: { minMilestone: 'UNDER_CONTRACT' },
    },
    {
      fieldKey: 'listingPrice',
      renderCondition: {
        representationTypes: [
          TRANSACTION_REPRESENTATION_TYPES.SELLER,
          TRANSACTION_REPRESENTATION_TYPES.BOTH,
        ],
        milestone: 'PRE_CONTRACT',
      },
    },
    {
      fieldKey: 'listingAgreementExpirationDate',
      label: 'Expiration Date',
      renderCondition: {
        representationTypes: [
          TRANSACTION_REPRESENTATION_TYPES.SELLER,
          TRANSACTION_REPRESENTATION_TYPES.BOTH,
        ],
        milestone: 'PRE_CONTRACT',
      },
    },
    {
      fieldKey: 'listingDate',
      renderCondition: {
        representationTypes: [TRANSACTION_REPRESENTATION_TYPES.SELLER],
        milestone: 'PRE_CONTRACT',
      },
    },
  ],
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    COMPLIANCE_DAILY_HUB_MODULE,
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.COMPLIANCE_REPORTING,
      enabled: true,
      roles: [ROLES.ADMIN, ROLES.COMPLIANCE_ADMIN],
    },
  ],
};

// Select Real Estate — basic onboarding stub (no integrations yet).
// Overrides only; DEFAULT_CONFIG is merged in by getPartnerConfig.
const SELECT_REAL_ESTATE_CONFIG: PartnerConfig = {
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/New_York',
  enableComplianceDocumentUpload: true,
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  externalSourceLink: { show: false, label: 'Deal Number' },
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
  ],
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.OTHER,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
    TRANSACTION_REPRESENTATION_TYPES.OTHER,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OTHER,
  ],
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
};

const CENTURY21_REDWOOD_CONFIG: PartnerConfig = {
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/New_York',
  enableComplianceDocumentUpload: true,
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  externalSourceLink: { show: true, label: 'Paperless Link' },
  externalSourceResync: true,
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    COMPLIANCE_DAILY_HUB_MODULE,
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.EMAIL_TRIAGE,
      enabled: true,
      roles: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
    },
  ],
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.NEW_CONSTRUCTION,
    TRANSACTION_PROPERTY_TYPES.LAND_LOT,
    TRANSACTION_PROPERTY_TYPES.OTHER,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
    TRANSACTION_REPRESENTATION_TYPES.OTHER,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.NEW_CONSTRUCTION,
    TRANSACTION_DEAL_TYPES.OTHER,
  ],
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
};

const MAXHOME_CONFIG: PartnerConfig = {
  moduleConfigs: DEFAULT_CONFIG.moduleConfigs,
  excludedMilestonesByTransactionType: {
    LEASE_LISTING: ['CLOSING'],
    LEASE_OFFER: ['CLOSING'],
  },
  externalSourceLink: { show: true, label: 'Dotloop Link' },
};

const LAMACCHIA_REALTY_CONFIG: PartnerConfig = {
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/New_York',
  enableComplianceDocumentUpload: true,
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  externalSourceLink: { show: false, label: 'Deal Number' },
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.PARTNER_LOOPS,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
  ],
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.OTHER,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
    TRANSACTION_REPRESENTATION_TYPES.OTHER,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OTHER,
  ],
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
};

const SMITH_AND_ASSOCIATES_CONFIG: PartnerConfig = {
  canEditClosingDate: [ROLES.ADMIN, ROLES.COMPLIANCE, ROLES.COMPLIANCE_ADMIN],
  timezone: 'America/New_York',
  enableComplianceDocumentUpload: true,
  showFolderView: [ROLES.AGENT, ROLES.COMPLIANCE],
  externalSourceLink: { show: false, label: 'Deal Number' },
  agreementForms: [
    {
      key: 'smith-and-associates-buyer-v1',
      label: 'Buyer Brokerage Agreement',
      path: AppURLs.agentIntake('smith-and-associates-buyer-v1'),
    },
    {
      key: 'smith-and-associates-listing-v1',
      label: 'Listing Agreement',
      path: AppURLs.agentIntake('smith-and-associates-listing-v1'),
    },
  ],
  moduleConfigs: [
    {
      id: MODULE_IDS.TRANSACTIONS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.OFFICES_AND_TEAMS,
      enabled: true,
      roles: [ROLES.COMPLIANCE_ADMIN, ROLES.VIEWER],
    },
    {
      id: MODULE_IDS.TASKS,
      enabled: true,
      roles: ['*'],
    },
    {
      id: MODULE_IDS.TASK_TEMPLATES,
      enabled: true,
      roles: [ROLES.ADMIN],
    },
  ],
  transactionTypeV2Enabled: true,
  propertyTypes: [
    TRANSACTION_PROPERTY_TYPES.RESIDENTIAL,
    TRANSACTION_PROPERTY_TYPES.OTHER,
  ],
  representationTypes: [
    TRANSACTION_REPRESENTATION_TYPES.BUYER,
    TRANSACTION_REPRESENTATION_TYPES.SELLER,
    TRANSACTION_REPRESENTATION_TYPES.BOTH,
    TRANSACTION_REPRESENTATION_TYPES.OTHER,
  ],
  dealTypes: [
    TRANSACTION_DEAL_TYPES.PURCHASE,
    TRANSACTION_DEAL_TYPES.LEASE,
    TRANSACTION_DEAL_TYPES.OTHER,
  ],
  useGroupTaskTemplates: [
    ROLES.ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
};

const PARTNER_CONFIG_BY_NAME_MAP: Record<string, PartnerConfig> = {
  [PARTNERS.ARC_REALTY]: ARC_REALTY_CONFIG,
  [PARTNERS.MAXHOME]: MAXHOME_CONFIG,
  [PARTNERS.BAIRD_AND_WARNER]: BAIRD_AND_WARNER_PARTNER_CONFIG,
  [PARTNERS.CAPSTONE]: CAPSTONE_CONFIG,
  [PARTNERS.CUTLER_REAL_ESTATE]: CUTLER_PARTNER_CONFIG,
  [PARTNERS.HELEN_ADAMS_REALTY]: HELEN_ADAMS_REALTY_CONFIG,
  [PARTNERS.KEYES_COMPANY]: KEYES_PARTNER_CONFIG,
  [PARTNERS.RW_TOWNE]: RW_TOWNE_CONFIG,
  [PARTNERS.TOMLINSON]: TOMLINSON_CONFIG,
  [PARTNERS.WATSON_REALTY]: WATSON_CONFIG,
  [PARTNERS.GARY_GREENE]: GARY_GREENE_CONFIG,
  [PARTNERS.COASTAL]: COASTAL_CONFIG,
  [PARTNERS.REAL_ESTATE_ONE]: REAL_ESTATE_ONE_CONFIG,
  [PARTNERS.SELECT_REAL_ESTATE]: SELECT_REAL_ESTATE_CONFIG,
  [PARTNERS.CENTURY21_REDWOOD]: CENTURY21_REDWOOD_CONFIG,
  [PARTNERS.JOHN_R_WOOD]: JOHN_R_WOOD_CONFIG,
  [PARTNERS.LAMACCHIA_REALTY]: LAMACCHIA_REALTY_CONFIG,
  [PARTNERS.SMITH_AND_ASSOCIATES]: SMITH_AND_ASSOCIATES_CONFIG,
};

// Inject the Admin-only default DailyHub unless the partner already declares its
// own (partners on COMPLIANCE_DAILY_HUB_MODULE supply theirs and are skipped).
const withDefaultDailyHub = (moduleConfigs: ModuleConfig[]): ModuleConfig[] => {
  if (moduleConfigs.some((module) => module.id === MODULE_IDS.DAILY_HUB)) {
    return moduleConfigs;
  }
  // Place DailyHub right after Transactions (matching the existing nav order),
  // falling back to the front if Transactions isn't present.
  const transactionsIndex = moduleConfigs.findIndex(
    (module) => module.id === MODULE_IDS.TRANSACTIONS
  );
  const insertAt = transactionsIndex === -1 ? 0 : transactionsIndex + 1;
  return [
    ...moduleConfigs.slice(0, insertAt),
    DEFAULT_DAILY_HUB_MODULE,
    ...moduleConfigs.slice(insertAt),
  ];
};

/**
 * Default Remarks Inbox module — enabled for every partner via
 * getPartnerConfig. Partners may define their own REMARKS_INBOX module to
 * override the default roles. Visibility is still gated by the
 * enable_remarks_inbox feature flag in the compliance dashboard.
 */
const DEFAULT_REMARKS_INBOX_MODULE: ModuleConfig = {
  id: MODULE_IDS.REMARKS_INBOX,
  enabled: true,
  roles: ['*'],
};

const withDefaultRemarksInbox = (
  moduleConfigs: ModuleConfig[]
): ModuleConfig[] => {
  if (moduleConfigs.some((module) => module.id === MODULE_IDS.REMARKS_INBOX)) {
    return moduleConfigs;
  }
  return [...moduleConfigs, DEFAULT_REMARKS_INBOX_MODULE];
};

// Every partner gets Support, admins only; a partner SUPPORT entry widens it.
const DEFAULT_SUPPORT_MODULE: ModuleConfig = {
  id: MODULE_IDS.SUPPORT,
  enabled: true,
  roles: [ROLES.ADMIN],
};

const withDefaultSupport = (moduleConfigs: ModuleConfig[]): ModuleConfig[] => {
  if (moduleConfigs.some((module) => module.id === MODULE_IDS.SUPPORT)) {
    return moduleConfigs;
  }
  return [...moduleConfigs, DEFAULT_SUPPORT_MODULE];
};

// Every partner gets Help, admins only; a partner HELP entry widens it.
const DEFAULT_HELP_MODULE: ModuleConfig = {
  id: MODULE_IDS.HELP,
  enabled: true,
  roles: [ROLES.ADMIN],
};

const withDefaultHelp = (moduleConfigs: ModuleConfig[]): ModuleConfig[] => {
  if (moduleConfigs.some((module) => module.id === MODULE_IDS.HELP)) {
    return moduleConfigs;
  }
  return [...moduleConfigs, DEFAULT_HELP_MODULE];
};

const DEFAULT_ROSTER_MODULE: ModuleConfig = {
  id: MODULE_IDS.ROSTER,
  enabled: true,
  roles: [
    ROLES.ADMIN,
    ROLES.AGENT_ADMIN,
    ROLES.OFFICE_ADMIN,
    ROLES.COMPLIANCE,
    ROLES.COMPLIANCE_ADMIN,
  ],
};

const withDefaultRoster = (moduleConfigs: ModuleConfig[]): ModuleConfig[] => {
  if (moduleConfigs.some((module) => module.id === MODULE_IDS.ROSTER)) {
    return moduleConfigs;
  }
  return [...moduleConfigs, DEFAULT_ROSTER_MODULE];
};

const DEFAULT_FORMS_LIBRARY_MODULE: ModuleConfig = {
  id: MODULE_IDS.FORMS_LIBRARY,
  enabled: true,
  roles: [ROLES.ADMIN],
};

const withDefaultFormsLibrary = (
  moduleConfigs: ModuleConfig[]
): ModuleConfig[] => {
  if (moduleConfigs.some((module) => module.id === MODULE_IDS.FORMS_LIBRARY)) {
    return moduleConfigs;
  }
  const helpIndex = moduleConfigs.findIndex(
    (module) => module.id === MODULE_IDS.HELP
  );
  const insertAt = helpIndex === -1 ? moduleConfigs.length : helpIndex;
  return [
    ...moduleConfigs.slice(0, insertAt),
    DEFAULT_FORMS_LIBRARY_MODULE,
    ...moduleConfigs.slice(insertAt),
  ];
};

/** Apply every all-partner default module to a partner's moduleConfigs. */
const withDefaultModules = (moduleConfigs: ModuleConfig[]): ModuleConfig[] =>
  [
    withDefaultDailyHub,
    withDefaultRemarksInbox,
    withDefaultSupport,
    withDefaultHelp,
    withDefaultRoster,
    withDefaultFormsLibrary,
  ].reduce((configs, applyDefault) => applyDefault(configs), moduleConfigs);

/**
 * Get partner configuration by partner ID or company name
 * @param partnerId - The partner ID (ObjectID) or company name
 * @returns PartnerConfig object
 */
export const getPartnerConfig = (companyName: string): PartnerConfig => {
  const partnerConfig = PARTNER_CONFIG_BY_NAME_MAP[companyName];
  if (!partnerConfig) {
    return {
      ...DEFAULT_CONFIG,
      moduleConfigs: withDefaultModules(DEFAULT_CONFIG.moduleConfigs),
    };
  }
  // Merge with defaults so partners only need to override what they want to change
  const mergedConfig = { ...DEFAULT_CONFIG, ...partnerConfig };
  // Partner-defined fields win; append default common fields for uncovered keys.
  const partnerFields = partnerConfig.extraSOTFields || [];
  const partnerKeys = new Set(
    partnerFields.flatMap((f) => [f.fieldKey, ...(f.aliasKeys ?? [])])
  );
  const commonFields = (DEFAULT_CONFIG.extraSOTFields || []).filter(
    (f) => ![f.fieldKey, ...(f.aliasKeys ?? [])].some((k) => partnerKeys.has(k))
  );
  return {
    ...mergedConfig,
    extraSOTFields: [...partnerFields, ...commonFields],
    moduleConfigs: withDefaultModules(mergedConfig.moduleConfigs),
  };
};

/**
 * Returns the user's roles for permission checking purposes.
 * CLOSING_MEMBER inherits all COMPLIANCE permissions unless the given key
 * is listed in closingMemberExclusions for the current partner config.
 */
export const getEffectiveRoles = (key?: string): string[] => {
  const roles = getUserRoles();
  if (
    roles.includes(ROLES.CLOSING_MEMBER) &&
    !roles.includes(ROLES.COMPLIANCE)
  ) {
    const companyName = getPartnerData()?.companyName ?? '';
    const config = getPartnerConfig(companyName);
    if (!key || !config.closingMemberExclusions?.includes(key)) {
      return [...roles, ROLES.COMPLIANCE];
    }
  }
  return roles;
};

export const canAccessModule = (moduleId: string): boolean => {
  const config = getPartnerConfig(
    getPartnerCompanyName() || ''
  ).moduleConfigs.find((module) => module.id === moduleId);
  if (!config?.enabled) return false;
  const roles = getEffectiveRoles();
  return (
    config.roles.includes('*') ||
    config.roles.some((role) => roles.includes(role))
  );
};

/**
 * Get a specific config value for a partner with role-based access check
 * @param key - The config key to retrieve (e.g., 'canEditClosingDate')
 * @returns boolean indicating if the role has access to the feature, or undefined if config doesn't exist
 */
export const getPartnerConfigValue = (key: string): boolean => {
  const companyName = getPartnerData()?.companyName;
  const roles = getEffectiveRoles(key);

  if (!companyName) {
    return false;
  }

  if (!roles) {
    return false;
  }

  const config = getPartnerConfig(companyName);

  const allowedRoles = config[key as keyof PartnerConfig] as
    | string[]
    | undefined;

  if (!allowedRoles || allowedRoles.length === 0) {
    return false;
  }

  return roles.some((role: string) => allowedRoles.includes(role));
};

/**
 * Whether we should prefer errorFields vertices for this partner over document
 * base config coordinates for document-error overlays. Defaults to true; only
 * partners that explicitly set `preferApiErrorVertices: false` opt out.
 */
export const shouldPreferApiErrorVertices = (): boolean => {
  const companyName = getPartnerData()?.companyName ?? '';
  return getPartnerConfig(companyName).preferApiErrorVertices !== false;
};

export const isProcessorReviewByEmail = (): boolean => {
  const companyName = getPartnerData()?.companyName ?? '';
  return (
    getPartnerConfig(companyName).emailProcessorReviewWithoutCompliance ===
      true && !getPartnerConfigValue('showComplianceCheck')
  );
};

// No role check — the public signer route has no signed-in user, so it reads DEFAULT_CONFIG.
export const isInhouseSigningPartnerEnabled = (): boolean => {
  const companyName = getPartnerData()?.companyName ?? '';
  return getPartnerConfig(companyName).enableInhouseSigning === true;
};

// Partner half of the gate for transaction deep links in agent-facing emails;
// the enable_transaction_link_in_agent_emails feature flag is the other half.
export const isTransactionLinkInAgentEmailsPartnerEnabled = (): boolean => {
  const companyName = getPartnerData()?.companyName ?? '';
  return (
    getPartnerConfig(companyName).showTransactionLinkInAgentEmails === true
  );
};

// The report is pinned to the caller's Agent profile, so a role granted here also needs one.
export const canViewAgentReporting = (): boolean =>
  getPartnerConfigValue('showAgentReporting') && Boolean(getAgentProfile());

// Sole gate on showing the Payments tab.
export const isPaymentsPartnerEnabled = (): boolean => {
  const companyName = getPartnerData()?.companyName ?? '';
  return getPartnerConfig(companyName).enablePayments === true;
};

// Partner half of the daily_hub_v2 gate. The three surfaces it covers — hub, document viewer, task
// detail — hand work to each other, so they are enabled together or not at all.
export const isDailyHubV2PartnerEnabled = (): boolean => {
  const companyName = getPartnerData()?.companyName ?? '';
  return getPartnerConfig(companyName).enableDailyHubV2 === true;
};

// Partner half of the gate on the documents checklist replacing Required Docs on the transaction
// page. Deliberately separate: this one can ship without the rest of the redesign.
export const isDocumentChecklistPartnerEnabled = (): boolean => {
  const companyName = getPartnerData()?.companyName ?? '';
  return getPartnerConfig(companyName).enableDocumentChecklist === true;
};

export const isAskMaxPartnerEnabled = (): boolean => {
  const companyName = getPartnerData()?.companyName ?? '';
  return getPartnerConfig(companyName).enableAskMax === true;
};

// Agents may continue to signing with unresolved required compliance errors.
export const isSigningWithComplianceErrorsAllowed = (): boolean => {
  const companyName = getPartnerData()?.companyName ?? '';
  return (
    getPartnerConfig(companyName).allowSigningWithComplianceErrors === true
  );
};

// Not hoistable to a module constant — partnerData lands in localStorage asynchronously.
export const getPartnerTimeZone = (): string => {
  const companyName = getPartnerData()?.companyName ?? '';
  return getPartnerConfig(companyName).timezone ?? 'America/New_York';
};

/**
 * Get a nested taskConfigs value with role-based access check.
 * @param key - key inside PartnerConfig.taskConfigs (e.g. 'addTaskInTransaction')
 * @returns whether the current user's role is allowed for that task feature
 */
export const getTaskConfigValue = (
  key: keyof NonNullable<PartnerConfig['taskConfigs']>
): boolean => {
  const companyName = getPartnerData()?.companyName;
  const roles = getEffectiveRoles(key);

  if (!companyName || !roles) {
    return false;
  }

  const config = getPartnerConfig(companyName);
  const allowedRoles = config.taskConfigs?.[key];

  if (!allowedRoles || allowedRoles.length === 0) {
    return false;
  }

  return roles.some((role: string) => allowedRoles.includes(role));
};
